import './LeaderboardBackToOG.css';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react"
import { useLocation } from 'react-router-dom';
import { enrichWithPreviousLeaderboard, fetchUnifiedLeaderboardData, parseExcludedSessionIds } from '../../shared/leaderboardShared';
import { fetchDemoLeaderboardData } from './backToOgDemo';
import logo from '../../overlays/back-to-og/logo.png';

const TEAMS_PER_PAGE = 20;
const TEAMS_PER_COLUMN = 10;
const ROW_STEP = 72;
const NAME_FONT_MAX = 26;
const NAME_FONT_MIN = 14;
const REFRESH_MS = 15000;
const DEMO_REFRESH_MS = 4000;
const PAGE_TITLE = 'Leaderboard Back To OG';
const BLANK_ICON = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function isTrue(value) {
    return value === 'true' || value === '1';
}

function PositionChange({ value }) {
    if (value === null || value === undefined) return null;
    if (value === 0) return <span className='bog_change bog_change_same'>=</span>;
    return value > 0
        ? <span className='bog_change bog_change_up'>+{value}</span>
        : <span className='bog_change bog_change_down'>{value}</span>;
}

const Row = React.memo(function Row({ rank, teamname, points, elims, avgPlace, wins, games, showGamesColumn, onClick, positionChange, showPositionIndicators, refreshDelta, animationEnabled, cascadeFadeEnabled, cascadeIndex, alive, dimmed, showFlags, memberData }) {
    const nameRef = useRef(null);
    const [nameFit, setNameFit] = useState({ size: NAME_FONT_MAX, scaleX: 1 });
    const hasFlagMembers = showFlags && memberData && memberData.length > 0;

    useLayoutEffect(() => {
        const element = nameRef.current;
        const container = element?.parentElement;
        if (!element || !container) return;

        const style = window.getComputedStyle(container);
        const available = container.clientWidth
            - (Number.parseFloat(style.paddingLeft) || 0)
            - (Number.parseFloat(style.paddingRight) || 0);
        const previousSize = element.style.fontSize;
        const previousTransform = element.style.transform;
        element.style.fontSize = `${NAME_FONT_MAX}px`;
        element.style.transform = 'none';
        const needed = element.getBoundingClientRect().width * 1.03;
        element.style.fontSize = previousSize;
        element.style.transform = previousTransform;

        if (available <= 0 || needed <= available) {
            setNameFit({ size: NAME_FONT_MAX, scaleX: 1 });
            return;
        }
        const size = Math.max(NAME_FONT_MIN, Math.floor(NAME_FONT_MAX * available / needed));
        const neededAtSize = needed * size / NAME_FONT_MAX;
        setNameFit({ size, scaleX: neededAtSize > available ? Math.max(0.5, available / neededAtSize) : 1 });
    }, [teamname, memberData, showFlags]);

    const getAnimationStyle = () => {
        if (!animationEnabled || !refreshDelta) return {};

        const distance = Math.abs(refreshDelta) * ROW_STEP;
        const duration = Math.max(0.6, Math.min(2.0, distance / 200));

        return {
            '--slide-from': `${refreshDelta > 0 ? distance : -distance}px`,
            animation: `bogSlideIn ${duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            zIndex: 10,
        };
    };

    const rowClassName = [
        'bog_row',
        rank <= 3 ? 'bog_is_podium' : '',
        dimmed ? 'bog_is_dead' : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={rowClassName}
            style={cascadeFadeEnabled ? {
                opacity: 0,
                animation: `bogFadeIn 0.6s ${cascadeIndex * 0.06}s forwards`,
            } : getAnimationStyle()}
        >
            <div className='bog_row_bar'>
                <span className='bog_row_rank_tile' />
                <span className='bog_row_stats_band' />
                <span className='bog_row_points_tile' />
            </div>
            <div className='bog_cell bog_cell_rank'>{rank}</div>
            <div className='bog_cell bog_cell_status'>
                {alive
                    ? <span className='bog_alive_dot' />
                    : showPositionIndicators && games >= 2 && <PositionChange value={positionChange} />}
            </div>
            <div className='bog_cell bog_cell_name' onClick={onClick}>
                <span
                    ref={nameRef}
                    className='bog_team_name'
                    style={{
                        fontSize: `${nameFit.size}px`,
                        transform: nameFit.scaleX < 1 ? `scaleX(${nameFit.scaleX})` : undefined,
                    }}
                >
                    {hasFlagMembers ? (
                        memberData.map((member, index) => (
                            <span key={index} className='bog_member'>
                                <img
                                    src={member.flagUrl}
                                    alt=''
                                    className='bog_flag'
                                    onError={(event) => {
                                        event.target.onerror = null;
                                        event.target.src = member.fallbackFlagUrl;
                                    }}
                                />
                                <span>{member.name}</span>
                                {index < memberData.length - 1 && <span className='bog_separator'>-</span>}
                            </span>
                        ))
                    ) : (
                        teamname
                    )}
                </span>
            </div>
            <div className='bog_cell bog_cell_stat'>{avgPlace.toFixed(1)}</div>
            <div className='bog_cell bog_cell_stat'>{elims}</div>
            <div className='bog_cell bog_cell_stat'>{wins}</div>
            {showGamesColumn && <div className='bog_cell bog_cell_stat'>{games}</div>}
            <div className='bog_cell bog_cell_points'>{points}</div>
        </div>
    );
});

function LeaderboardBackToOG() {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const leaderboard_id = urlParams.get('id');
    const demoMode = isTrue(urlParams.get('demo'));
    const showFlags = isTrue(urlParams.get('flags'));
    const showGamesColumn = isTrue(urlParams.get('games'));
    const titleText = urlParams.get('title') || 'Classement';
    const subtitleText = urlParams.get('subtitle') || '';
    const rotateSeconds = Number.parseInt(urlParams.get('rotate') || '', 10);
    const excludedSessionIds = useMemo(() => parseExcludedSessionIds(new URLSearchParams(location.search)), [location.search]);
    const excludedSessionIdsKey = useMemo(() => Array.from(excludedSessionIds).sort().join(','), [excludedSessionIds]);

    const [leaderboard, setLeaderboard] = useState([]);
    const [teamDetails, setTeamDetails] = useState({});
    const [page, setPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [showPositionIndicators, setShowPositionIndicators] = useState(false);
    const [animationEnabled, setAnimationEnabled] = useState(false);
    const [cascadeFadeEnabled, setCascadeFadeEnabled] = useState(isTrue(urlParams.get('cascade')));
    const previousLeaderboardRef = useRef(null);

    const loadLeaderboard = async () => {
        if (!demoMode && !leaderboard_id) return;
        try {
            const data = demoMode
                ? fetchDemoLeaderboardData()
                : await fetchUnifiedLeaderboardData({
                    leaderboardId: leaderboard_id,
                    excludedSessionIds,
                    showFlags,
                    forceRankByPoints: true,
                    includeV7: true,
                    indicatorsOnlyWhenAllDead: true,
                });

            setShowPositionIndicators(data.showPositionIndicators);

            const previousData = previousLeaderboardRef.current;
            const merged = enrichWithPreviousLeaderboard(data.leaderboard, previousData);
            const previousPlaces = new Map((previousData || []).map((team) => [team.teamname, team.place]));
            const rows = merged.leaderboard.map((team) => {
                const previousPlace = previousPlaces.get(team.teamname);
                return {
                    ...team,
                    refreshDelta: typeof previousPlace === 'number' ? previousPlace - team.place : 0,
                };
            });

            setLeaderboard(rows);
            setTeamDetails(data.teamDetails);
            previousLeaderboardRef.current = rows;

            if (previousData && merged.changedCount > 0) {
                setAnimationEnabled(true);
                setTimeout(() => { setAnimationEnabled(false); }, 2500);
            } else {
                setAnimationEnabled(false);
            }
        } catch (error) {
            console.error('Error loading leaderboard data:', error);
        }
    };

    useEffect(() => {
        const previousTitle = document.title;
        const icons = Array.from(document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']"));
        const previousHrefs = icons.map((icon) => icon.getAttribute('href'));
        document.title = PAGE_TITLE;
        icons.forEach((icon) => icon.setAttribute('href', BLANK_ICON));
        return () => {
            document.title = previousTitle;
            icons.forEach((icon, index) => icon.setAttribute('href', previousHrefs[index]));
        };
    }, []);

    useEffect(() => {
        previousLeaderboardRef.current = null;
        loadLeaderboard();
        const interval = setInterval(loadLeaderboard, demoMode ? DEMO_REFRESH_MS : REFRESH_MS);
        return () => clearInterval(interval);
    }, [leaderboard_id, demoMode, showFlags, excludedSessionIdsKey]);

    const filteredLeaderboard = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return leaderboard;
        return leaderboard.filter((team) => {
            if (team.teamname.toLowerCase().includes(query)) return true;
            if (!Number.isNaN(Number(query)) && team.place === Number(query)) return true;
            return (teamDetails[team.teamname]?.members || []).some((member) =>
                String(member.ingame_name || '').toLowerCase().includes(query)
            );
        });
    }, [leaderboard, teamDetails, searchQuery]);

    const pageCount = Math.max(1, Math.ceil(filteredLeaderboard.length / TEAMS_PER_PAGE));
    const currentPage = Math.min(page, pageCount - 1);

    const nextPage = () => setPage((current) => (current + 1) % pageCount);
    const previousPage = () => setPage((current) => (current - 1 + pageCount) % pageCount);

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    useEffect(() => {
        if (!Number.isFinite(rotateSeconds) || rotateSeconds <= 0 || pageCount <= 1) return undefined;
        const timeoutId = setTimeout(() => {
            setPage((current) => (current + 1) % pageCount);
        }, rotateSeconds * 1000);
        return () => clearTimeout(timeoutId);
    }, [rotateSeconds, pageCount, currentPage]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'F1') {
                event.preventDefault();
                setShowSearch((previous) => !previous);
            } else if (event.key === 'F8') {
                event.preventDefault();
                setCascadeFadeEnabled((previous) => !previous);
            } else if (event.key === 'ArrowRight' && event.target.tagName !== 'INPUT') {
                setPage((current) => (current + 1) % pageCount);
            } else if (event.key === 'ArrowLeft' && event.target.tagName !== 'INPUT') {
                setPage((current) => (current - 1 + pageCount) % pageCount);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [pageCount]);

    const pageStart = currentPage * TEAMS_PER_PAGE;
    const leftItems = filteredLeaderboard.slice(pageStart, pageStart + TEAMS_PER_COLUMN);
    const rightItems = filteredLeaderboard.slice(pageStart + TEAMS_PER_COLUMN, pageStart + TEAMS_PER_PAGE);
    const hasAliveTeams = leaderboard.some((team) => team.alive);
    const selectedDetails = selectedTeam ? teamDetails[selectedTeam] : null;
    const selectedSessions = [...(selectedDetails?.sessions || [])]
        .sort((a, b) => (Number(a.start_time) || 0) - (Number(b.start_time) || 0));
    const finishedPlaces = selectedSessions.map((session) => session.place).filter((place) => place > 0);

    const renderHeader = () => (
        <div className='bog_header'>
            <div className='bog_header_bar' />
            <div className='bog_cell bog_cell_rank bog_is_clickable' onClick={previousPage}>#</div>
            <div className='bog_cell bog_cell_status' />
            <div className='bog_cell bog_cell_name'>Équipe</div>
            <div className='bog_cell bog_cell_stat'>Moy.</div>
            <div className='bog_cell bog_cell_stat'>Élims</div>
            <div className='bog_cell bog_cell_stat'>Top 1</div>
            {showGamesColumn && <div className='bog_cell bog_cell_stat'>Games</div>}
            <div className='bog_cell bog_cell_points bog_is_clickable' onClick={nextPage}>Pts</div>
        </div>
    );

    const renderColumn = (items, offset) => (
        <div className='bog_column'>
            {renderHeader()}
            {items.map((data, index) => (
                <Row
                    key={`${data.teamId || data.teamname}-${data.place}`}
                    rank={data.place}
                    teamname={data.teamname}
                    points={data.points}
                    elims={data.elims}
                    wins={data.wins}
                    games={data.games}
                    avgPlace={data.avg_place}
                    showGamesColumn={showGamesColumn}
                    onClick={() => setSelectedTeam(data.teamname)}
                    positionChange={data.positionChange}
                    showPositionIndicators={showPositionIndicators}
                    refreshDelta={data.refreshDelta || 0}
                    animationEnabled={animationEnabled && data.hasPositionChanged}
                    cascadeFadeEnabled={cascadeFadeEnabled}
                    cascadeIndex={offset + index}
                    alive={data.alive}
                    dimmed={hasAliveTeams && !data.alive}
                    showFlags={showFlags}
                    memberData={data.memberData}
                />
            ))}
        </div>
    );

    return (
        <div className={`back_to_og${showGamesColumn ? ' bog_has_games' : ''}`}>
            <div className='bog_canvas'>
                <div className='bog_layer bog_edge' />
                <div className='bog_layer bog_neon' />
                <div className='bog_layer bog_body'>
                    <svg className='bog_shards' viewBox='0 0 1920 1080' width='1920' height='1080'>
                        <polygon points='1180,0 1920,0 1920,330' fill='#222d68' />
                        <polygon points='1500,0 1720,0 1920,120 1920,230' fill='#3f4c93' opacity='.45' />
                        <polygon points='0,90 640,0 760,0 0,330' fill='#1c2660' />
                        <polygon points='0,40 260,0 420,0 0,170' fill='#2b3778' opacity='.5' />
                        <polygon points='0,600 1920,330 1920,470 0,780' fill='#1c2660' opacity='.55' />
                        <polygon points='1920,760 1920,1080 1340,1080' fill='#222d68' />
                        <polygon points='0,880 560,1080 0,1080' fill='#1c2660' />
                        <polygon points='1920,520 1920,640 1500,600' fill='#2b3778' opacity='.5' />
                    </svg>
                    <div className='bog_dots' />
                    <div className='bog_dots bog_dots_top' />
                    <span className='bog_brush' style={{ left: 1380, top: 40, width: 520, transform: 'rotate(22deg)' }} />
                    <span className='bog_brush' style={{ left: -40, top: 250, width: 420, transform: 'rotate(-14deg)' }} />
                    <span className='bog_brush' style={{ left: 1200, top: 1010, width: 600, transform: 'rotate(-9deg)' }} />
                </div>
                <svg className='bog_sparks' width='1920' height='260' viewBox='0 0 1920 260'>
                    <polygon points='770,170 812,186 780,198' fill='#4fe3a8' />
                    <polygon points='1150,150 1112,176 1144,180' fill='#4fe3a8' />
                    <polygon points='800,96 842,114 810,120' fill='#7fe9ff' opacity='.85' />
                    <polygon points='1128,70 1092,96 1124,96' fill='#7fe9ff' opacity='.85' />
                </svg>

                <div className='bog_content'>
                    {showSearch && (
                        <div className='bog_search'>
                            <input
                                type='text'
                                className='bog_search_input'
                                value={searchQuery}
                                placeholder='Rechercher un joueur'
                                onChange={(event) => setSearchQuery(event.target.value)}
                                autoFocus
                            />
                        </div>
                    )}

                    <header className='bog_top_bar'>
                        <div className='bog_title_block'>
                            <span className='bog_title_main'>{titleText}</span>
                            {subtitleText && <span className='bog_title_sub'>{subtitleText}</span>}
                        </div>
                        <img className='bog_logo' src={logo} alt='Back to OG' />
                    </header>

                    <div className='bog_columns'>
                        {renderColumn(leftItems, 0)}
                        {rightItems.length > 0 && renderColumn(rightItems, TEAMS_PER_COLUMN)}
                    </div>

                    {pageCount > 1 && (
                        <div className='bog_pages'>
                            {Array.from({ length: pageCount }, (_, index) => (
                                <span
                                    key={index}
                                    className={`bog_page_dot${index === currentPage ? ' bog_is_active' : ''}`}
                                    onClick={() => setPage(index)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {selectedDetails && (
                    <div className='bog_modal_overlay' onClick={() => setSelectedTeam(null)}>
                        <div className='bog_modal' onClick={(event) => event.stopPropagation()}>
                            <button className='bog_modal_close' onClick={() => setSelectedTeam(null)}>×</button>
                            <div className='bog_modal_header'>
                                <span className='bog_modal_rank'>#{selectedDetails.teamData.place}</span>
                                <div className='bog_modal_members'>
                                    {selectedDetails.members.map((member, index) => (
                                        <span key={index} className='bog_modal_member'>{member.ingame_name || member.name}</span>
                                    ))}
                                </div>
                            </div>
                            <div className='bog_modal_stats'>
                                <div className='bog_modal_stat'><span className='bog_modal_value'>{selectedDetails.teamData.points}</span><span className='bog_modal_label'>Points</span></div>
                                <div className='bog_modal_stat'><span className='bog_modal_value'>{selectedSessions.length}</span><span className='bog_modal_label'>Games</span></div>
                                <div className='bog_modal_stat'><span className='bog_modal_value'>{selectedSessions.filter((session) => session.place === 1).length}</span><span className='bog_modal_label'>Top 1</span></div>
                                <div className='bog_modal_stat'><span className='bog_modal_value'>{selectedSessions.reduce((sum, session) => sum + (Number(session.kills) || 0), 0)}</span><span className='bog_modal_label'>Élims</span></div>
                                <div className='bog_modal_stat'><span className='bog_modal_value'>{finishedPlaces.length ? Math.min(...finishedPlaces) : '-'}</span><span className='bog_modal_label'>Meilleure place</span></div>
                            </div>
                            <div className='bog_sessions'>
                                <div className='bog_session bog_session_head'>
                                    <div>Game</div><div>Place</div><div>Élims</div>
                                </div>
                                {selectedSessions.map((session, index) => (
                                    <div key={index} className='bog_session'>
                                        <div>{index + 1}</div>
                                        <div className={session.place > 0 ? (session.place === 1 ? 'bog_place_win' : '') : 'bog_place_live'}>
                                            {session.place > 0 ? session.place : 'En jeu'}
                                        </div>
                                        <div>{session.kills}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LeaderboardBackToOG
